import { getPreviewUrl, getOriginalUrl } from '@/helpers/documentHelpers';
import { Box, useBreakpoint } from '@palmetto/palmetto-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useMemo, useRef, useState } from 'react';
import Lightbox, { NextIcon, PreviousIcon } from 'yet-another-react-lightbox';

import { GalleryItem } from './GalleryItem';
import 'yet-another-react-lightbox/styles.css';

type GalleryProps = {
  documents: any[];
  useLightbox?: boolean;
};

/** Threshold below which we skip virtualization (overhead not worth it for small galleries). */
const VIRTUALIZATION_THRESHOLD = 24;

/** Estimated height in pixels for a single gallery row (image + filename + padding). */
const ESTIMATED_ROW_HEIGHT = 160;

/** Number of extra rows to render above/below the visible area. */
const OVERSCAN_COUNT = 2;

/** Maximum height of the scrollable gallery container. */
const GALLERY_MAX_HEIGHT = '70vh';

function getColumnCount(isPhone: boolean, isDesktop: boolean, isHd: boolean): number {
  if (isHd) return 6;
  if (isPhone || isDesktop) return 4;
  return 2;
}

interface VirtualGalleryRowProps {
  rowIndex: number;
  columnCount: number;
  documents: any[];
  useLightbox: boolean;
  onDocumentClick: (index: number) => void;
}

const VirtualGalleryRow = ({
  rowIndex,
  columnCount,
  documents,
  useLightbox,
  onDocumentClick,
}: VirtualGalleryRowProps) => {
  const startIndex = rowIndex * columnCount;
  const cells = [];

  for (let col = 0; col < columnCount; col++) {
    const docIndex = startIndex + col;
    if (docIndex < documents.length) {
      cells.push(
        <GalleryItem
          item={documents[docIndex]}
          key={documents[docIndex].id}
          useLightbox={useLightbox}
          onClick={onDocumentClick}
          index={docIndex}
        />,
      );
    }
  }

  return <>{cells}</>;
};

export const Gallery = ({ documents, useLightbox = false }: GalleryProps) => {
  const { isPhone, isDesktop, isHd } = useBreakpoint();
  const [index, setIndex] = useState(-1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const columnCount = getColumnCount(isPhone, isDesktop, isHd);
  const rowCount = Math.ceil(documents.length / columnCount);
  const shouldVirtualize = documents.length > VIRTUALIZATION_THRESHOLD;

  const images = useMemo(() => {
    if (useLightbox) {
      return documents
        .filter((document) => document.files[0].contentType.includes('image'))
        .map((document) => {
          const viewUrls = document.files[0].viewUrls;
          const previewUrl = getPreviewUrl(viewUrls);
          const originalUrl = getOriginalUrl(viewUrls);
          return {
            id: document.id,
            src: previewUrl ?? originalUrl ?? '',
            original: originalUrl ?? '',
            thumbnailCaption: document.files[0].originalName,
          };
        });
    }
  }, [documents, useLightbox]);

  const handleDocumentClick = useCallback(
    (docIndex: number) => {
      if (useLightbox) {
        if (documents?.[docIndex]?.files[0].contentType.includes('image')) {
          const imageIndex = images?.findIndex((image) => image.id === documents[docIndex].id);
          if (imageIndex !== undefined && imageIndex > -1) {
            setIndex(imageIndex);
          }
        } else {
          const viewUrls = documents[docIndex].files[0].viewUrls;
          const documentUrl = getOriginalUrl(viewUrls);
          if (documentUrl) {
            window.open(documentUrl, '_blank');
          }
        }
      }
    },
    [useLightbox, documents, images],
  );

  const handleCloseLightbox = useCallback(() => setIndex(-1), []);

  const estimateSize = useCallback(() => ESTIMATED_ROW_HEIGHT, []);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize,
    overscan: OVERSCAN_COUNT,
  });

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(${columnCount},minmax(0,1fr))`,
      columnGap: 'var(--size-spacing-sm)',
      rowGap: 'var(--size-spacing-sm)',
    }),
    [columnCount],
  );

  const renderNonVirtualized = () => (
    <Box display="grid" style={gridStyle}>
      {documents.map((document, docIndex) => (
        <GalleryItem
          item={document}
          key={document.id}
          useLightbox={useLightbox}
          onClick={handleDocumentClick}
          index={docIndex}
        />
      ))}
    </Box>
  );

  const renderVirtualized = () => {
    const virtualRows = virtualizer.getVirtualItems();

    return (
      <div
        ref={scrollContainerRef}
        style={{
          maxHeight: GALLERY_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualRows.map((virtualRow) => (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <Box display="grid" style={gridStyle}>
                <VirtualGalleryRow
                  rowIndex={virtualRow.index}
                  columnCount={columnCount}
                  documents={documents}
                  useLightbox={useLightbox}
                  onDocumentClick={handleDocumentClick}
                />
              </Box>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Box>
      {shouldVirtualize ? renderVirtualized() : renderNonVirtualized()}
      {useLightbox ? (
        <Lightbox
          slides={images}
          open={index >= 0}
          index={index}
          close={handleCloseLightbox}
          carousel={{
            finite: images && images.length <= 1,
            padding: 50,
          }}
          render={{
            iconNext: () => (images && images.length <= 1 ? null : <NextIcon />),
            iconPrev: () => (images && images.length <= 1 ? null : <PreviousIcon />),
          }}
        />
      ) : null}
    </Box>
  );
};
